import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'sonner';
import QRCode from 'qrcode';

const KitchenQRCode = ({ restaurantId, restaurantName }) => {
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState('');
  const canvasRef = useRef(null);

  const baseUrl = window.location.origin;
  const kitchenDisplayUrl = `${baseUrl}/kitchen/${restaurantId}`;

  useEffect(() => {
    if (canvasRef.current && restaurantId) {
      generateQRCode();
    }
  }, [restaurantId]);

  const generateQRCode = async () => {
    try {
      const canvas = canvasRef.current;
      
      // Generate QR code using the qrcode library
      const dataUrl = await QRCode.toDataURL(kitchenDisplayUrl, {
        width: 256,
        margin: 2,
        color: {
          dark: '#000000',
          light: '#ffffff'
        },
        errorCorrectionLevel: 'H'
      });

      // Set the canvas content
      const ctx = canvas.getContext('2d');
      const img = new Image();
      img.onload = () => {
        canvas.width = 256;
        canvas.height = 256;
        ctx.drawImage(img, 0, 0);
      };
      img.src = dataUrl;
      
      setQrCodeDataUrl(dataUrl);
    } catch (error) {
      console.error('Error generating QR code:', error);
      toast.error('Failed to generate QR code');
    }
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(kitchenDisplayUrl);
      toast.success('Kitchen display URL copied to clipboard!');
    } catch (error) {
      console.error('Error copying to clipboard:', error);
      toast.error('Failed to copy URL');
    }
  };

  const downloadQRCode = () => {
    if (!qrCodeDataUrl) return;

    const link = document.createElement('a');
    link.download = `${restaurantName || 'Restaurant'}-Kitchen-Display-QR.png`;
    link.href = qrCodeDataUrl;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    toast.success('QR code downloaded successfully!');
  };

  const printQRCode = () => {
    if (!qrCodeDataUrl) return;

    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Kitchen Display QR Code - ${restaurantName || 'Restaurant'}</title>
          <style>
            body { 
              margin: 0; 
              padding: 20px; 
              font-family: Arial, sans-serif; 
              text-align: center;
              background: white;
            }
            .header { 
              margin-bottom: 30px; 
              border-bottom: 2px solid black; 
              padding-bottom: 20px; 
            }
            .qr-container { 
              margin: 30px 0; 
            }
            .footer { 
              margin-top: 30px; 
              font-size: 12px; 
              color: #666; 
            }
            @media print { 
              body { 
                margin: 0; 
                padding: 10px; 
              }
              @page { 
                size: A4; 
                margin: 1in; 
              }
            }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>🍳 Kitchen Display System</h1>
            <h2>${restaurantName || 'Restaurant'}</h2>
            <p>Scan this QR code to access the kitchen display</p>
          </div>
          
          <div class="qr-container">
            <img src="${qrCodeDataUrl}" alt="Kitchen Display QR Code" style="max-width: 300px; height: auto;" />
          </div>
          
          <div class="instructions">
            <h3>Instructions:</h3>
            <ol style="text-align: left; max-width: 400px; margin: 0 auto;">
              <li>Scan this QR code with any smartphone or tablet</li>
              <li>Or manually visit: <strong>${kitchenDisplayUrl}</strong></li>
              <li>The kitchen display will show live orders</li>
              <li>Use Mark Preparing, Mark Ready, Mark Completed buttons</li>
              <li>Display auto-refreshes every 10 seconds</li>
            </ol>
          </div>
          
          <div class="footer">
            <p>Kitchen Display System • Restaurant QR Ordering Platform</p>
            <p>Generated on: ${new Date().toLocaleDateString()}</p>
          </div>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
    
    toast.success('Print dialog opened for QR code!');
  };

  return (
    <div className="bg-white rounded-lg border shadow-sm p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">
          🍳 Kitchen Display System
        </h3>
        <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full font-medium">
          Live Updates
        </span>
      </div>

      <p className="text-sm text-gray-600 mb-6">
        Generate a QR code for your kitchen display system. Staff can scan this to access the live kitchen orders display with status update buttons.
      </p>

      <div className="grid md:grid-cols-2 gap-6">
        {/* QR Code Display */}
        <div className="text-center">
          <div className="bg-gray-50 p-4 rounded-lg inline-block mb-4">
            <canvas 
              ref={canvasRef}
              className="border border-gray-200 rounded"
              style={{ maxWidth: '100%', height: 'auto' }}
            />
          </div>
          <p className="text-xs text-gray-500">
            Kitchen Display QR Code
          </p>
        </div>

        {/* URL and Actions */}
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Kitchen Display URL:
            </label>
            <div className="flex">
              <input
                type="text"
                value={kitchenDisplayUrl}
                readOnly
                className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-l-lg bg-gray-50 text-gray-600"
              />
              <button
                onClick={copyToClipboard}
                className="px-3 py-2 text-sm bg-blue-600 text-white rounded-r-lg hover:bg-blue-700 border border-blue-600"
                title="Copy URL"
              >
                📋
              </button>
            </div>
          </div>

          <div className="space-y-3">
            <h4 className="text-sm font-medium text-gray-900">Actions:</h4>
            
            <button
              onClick={copyToClipboard}
              className="w-full px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center justify-center"
            >
              <span className="mr-2">🔗</span>
              Copy Kitchen Display Link
            </button>

            <button
              onClick={downloadQRCode}
              disabled={!qrCodeDataUrl}
              className="w-full px-4 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
            >
              <span className="mr-2">💾</span>
              Download QR Code
            </button>

            <button
              onClick={printQRCode}
              disabled={!qrCodeDataUrl}
              className="w-full px-4 py-2 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
            >
              <span className="mr-2">🖨️</span>
              Print QR Code
            </button>
          </div>
        </div>
      </div>

      {/* Usage Instructions */}
      <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
        <h4 className="text-sm font-semibold text-blue-800 mb-2">
          🔧 Kitchen Display Setup:
        </h4>
        <div className="text-sm text-blue-700 space-y-1">
          <p><strong>1.</strong> Print or display this QR code in your kitchen area</p>
          <p><strong>2.</strong> Kitchen staff can scan with any device to access the live display</p>
          <p><strong>3.</strong> Display shows active orders with priority indicators</p>
          <p><strong>4.</strong> Staff can update order status: Preparing → Ready → Completed</p>
          <p><strong>5.</strong> Auto-refreshes every 10 seconds for real-time updates</p>
        </div>
      </div>

      {/* Features List */}
      <div className="mt-4 grid md:grid-cols-2 gap-4 text-sm">
        <div>
          <h5 className="font-medium text-gray-900 mb-2">✨ Display Features:</h5>
          <ul className="text-gray-600 space-y-1">
            <li>• Live order updates (10s refresh)</li>
            <li>• Priority indicators for urgent orders</li>
            <li>• Clear item preparation details</li>
            <li>• Special requests highlighting</li>
          </ul>
        </div>
        <div>
          <h5 className="font-medium text-gray-900 mb-2">⚡ Quick Actions:</h5>
          <ul className="text-gray-600 space-y-1">
            <li>• One-click status updates</li>
            <li>• Table number prominence</li>
            <li>• Customer contact information</li>
            <li>• Payment status visibility</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default KitchenQRCode;